import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { useCart } from '../context/CartContext';
import { useT } from '../context/I18nContext';
import { toast } from 'sonner';
import { Minus, Plus, Truck, ShieldCheck, Bitcoin, Star } from 'lucide-react';
import ProductCard from '../components/ProductCard';
import { api } from '../api/client';

const Stars = ({ n = 0, size = 'h-4 w-4', interactive = false, onPick }) => (
  <div className="flex gap-0.5">
    {Array.from({ length: 5 }).map((_, i) => (
      interactive
        ? <button type="button" key={i} onClick={() => onPick && onPick(i + 1)} aria-label={`${i+1} star`}><Star className={`${size} ${i < n ? 'text-[#d4af37] fill-[#d4af37]' : 'text-zinc-700'}`} /></button>
        : <Star key={i} className={`${size} ${i < n ? 'text-[#d4af37] fill-[#d4af37]' : 'text-zinc-700'}`} />
    ))}
  </div>
);

const ProductDetail = () => {
  const { slug } = useParams();
  const { add } = useCart();
  const { t } = useT();
  const [qty, setQty] = useState(1);
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviews, setReviews] = useState([]);
  const [summary, setSummary] = useState({ avg: 0, count: 0 });
  const [reviewForm, setReviewForm] = useState({ name: '', email: '', rating: 5, title: '', body: '' });

  const loadReviews = async (productId) => {
    const [list, sum] = await Promise.all([
      api.listReviews({ product_id: productId, approved: true }),
      api.reviewSummary(productId),
    ]);
    setReviews(list);
    setSummary(sum);
  };

  useEffect(() => {
    setLoading(true);
    api.getProduct(slug)
      .then(async (p) => {
        setProduct(p);
        const all = await api.listProducts({ published: true });
        setRelated(all.filter((x) => x.id !== p.id).slice(0, 4));
        loadReviews(p.id);
      })
      .catch(() => setProduct(null))
      .finally(() => setLoading(false));
  }, [slug]);

  const submitReview = async (e) => {
    e.preventDefault();
    if (!reviewForm.name || !reviewForm.body) return toast.error('Name and review body are required');
    try {
      await api.createReview({ ...reviewForm, product_id: product.id });
      toast.success('Thanks for your review!');
      setReviewForm({ name: '', email: '', rating: 5, title: '', body: '' });
      loadReviews(product.id);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not submit'); }
  };

  if (loading) return (<><Header /><main className="bg-black min-h-[60vh] flex items-center justify-center"><p className="text-zinc-500">Loading…</p></main><Footer /></>);

  if (!product) return (
    <><Header /><main className="bg-black min-h-[60vh] flex items-center justify-center"><div className="text-center"><h1 className="serif-italic gold-gradient-text text-4xl">Product not found</h1><Link to="/shop" className="text-[#d4af37] mt-4 inline-block underline">Back to shop</Link></div></main><Footer /></>
  );

  const onSale = product.old_price && product.old_price > product.price;

  return (
    <>
      <Header />
      <main className="bg-black">
        <div className="max-w-[1480px] mx-auto px-6 py-16 grid lg:grid-cols-2 gap-12">
          <div className="relative aspect-square bg-zinc-950 rounded-md overflow-hidden border border-zinc-900">
            <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
          </div>
          <div>
            <p className="text-[#d4af37] tracking-[0.3em] text-xs uppercase mb-3">RandM · {product.category === '15k' ? '15K Tornado' : product.category === '20k' ? '20K Tornado' : 'Randm'}</p>
            <h1 className="serif-italic gold-gradient-text text-5xl">{product.name}</h1>
            {summary.count > 0 && (
              <div className="flex items-center gap-2 mt-3">
                <Stars n={Math.round(summary.avg)} />
                <span className="text-zinc-400 text-sm">{summary.avg} · {summary.count} review{summary.count > 1 ? 's' : ''}</span>
              </div>
            )}
            <div className="mt-4 text-2xl text-zinc-200">
              {onSale ? (<>
                <span className="line-through text-zinc-500 mr-2">€{product.old_price.toFixed(2)}</span>
                <span className="text-[#d4af37]">€{product.price.toFixed(2)}</span>
              </>) : <span className="text-[#d4af37]">€{product.price.toFixed(2)}</span>}
            </div>
            <p className="text-zinc-300 mt-6 leading-relaxed">{product.description}</p>
            <ul className="mt-6 grid grid-cols-2 gap-2 text-zinc-300 text-sm">
              <li><span className="text-[#d4af37]">✔</span> {(product.puffs||0).toLocaleString()} puffs</li>
              <li><span className="text-[#d4af37]">✔</span> Mesh coil</li>
              <li><span className="text-[#d4af37]">✔</span> USB-C rechargeable</li>
              <li><span className="text-[#d4af37]">✔</span> Adjustable airflow</li>
            </ul>

            <div className="mt-8 flex items-center gap-3">
              <div className="flex items-center border border-zinc-800 rounded">
                <button className="px-3 py-2 text-zinc-300 hover:text-[#d4af37]" onClick={() => setQty(Math.max(1, qty-1))} aria-label="Decrease"><Minus className="h-4 w-4" /></button>
                <span className="px-4 text-zinc-100">{qty}</span>
                <button className="px-3 py-2 text-zinc-300 hover:text-[#d4af37]" onClick={() => setQty(qty+1)} aria-label="Increase"><Plus className="h-4 w-4" /></button>
              </div>
              <Button className="btn-gold h-12 px-8 tracking-wider uppercase" onClick={() => { add(product, qty); toast.success(`${product.name} ×${qty} added`); }}>{t('product.add_to_cart')}</Button>
            </div>

            <div className="mt-8 grid grid-cols-3 gap-4 text-zinc-300 text-xs">
              <div className="flex items-center gap-2"><Truck className="h-4 w-4 text-[#d4af37]" /> {t('product.fast_shipping')}</div>
              <div className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-[#d4af37]" /> {t('product.authentic')}</div>
              <div className="flex items-center gap-2"><Bitcoin className="h-4 w-4 text-[#d4af37]" /> {t('product.pay_options')}</div>
            </div>
          </div>
        </div>

        {/* Reviews section */}
        <section className="max-w-[1480px] mx-auto px-6 pb-10 grid lg:grid-cols-[1fr,420px] gap-10">
          <div>
            <h3 className="serif-italic gold-gradient-text text-3xl mb-6">{t('product.reviews')}</h3>
            {reviews.length === 0 ? (
              <p className="text-zinc-500 text-sm">No reviews yet — be the first to leave one!</p>
            ) : (
              <div className="space-y-5">
                {reviews.map((r) => (
                  <div key={r.id} className="border border-zinc-900 rounded p-5">
                    <div className="flex items-center gap-3 mb-2">
                      <Stars n={r.rating} />
                      <span className="text-zinc-100 font-medium">{r.title || '(no title)'}</span>
                      {r.verified_buyer && <span className="text-emerald-400 text-xs border border-emerald-600 rounded px-1.5 py-0.5">Verified</span>}
                    </div>
                    <p className="text-zinc-300 text-sm leading-relaxed">{r.body}</p>
                    <p className="text-zinc-500 text-xs mt-2">— {r.name} · {(r.created_at || '').slice(0, 10)}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="border border-zinc-900 rounded p-6 bg-zinc-950 h-fit">
            <h4 className="serif-italic text-[#d4af37] text-xl mb-4">Write a review</h4>
            <form onSubmit={submitReview} className="space-y-3">
              <div>
                <Label className="text-zinc-300">Your rating</Label>
                <div className="mt-1"><Stars n={reviewForm.rating} size="h-6 w-6" interactive onPick={(n) => setReviewForm({ ...reviewForm, rating: n })} /></div>
              </div>
              <div><Label className="text-zinc-300">Name</Label><Input required value={reviewForm.name} onChange={(e) => setReviewForm({ ...reviewForm, name: e.target.value })} className="bg-transparent border-zinc-800 mt-1" /></div>
              <div><Label className="text-zinc-300">Email (optional)</Label><Input type="email" value={reviewForm.email} onChange={(e) => setReviewForm({ ...reviewForm, email: e.target.value })} className="bg-transparent border-zinc-800 mt-1" /></div>
              <div><Label className="text-zinc-300">Title</Label><Input value={reviewForm.title} onChange={(e) => setReviewForm({ ...reviewForm, title: e.target.value })} className="bg-transparent border-zinc-800 mt-1" /></div>
              <div><Label className="text-zinc-300">Your review</Label><Textarea required rows={4} value={reviewForm.body} onChange={(e) => setReviewForm({ ...reviewForm, body: e.target.value })} className="bg-transparent border-zinc-800 mt-1" /></div>
              <Button type="submit" className="btn-gold w-full h-11">Submit review</Button>
            </form>
          </div>
        </section>

        {related.length > 0 && (
          <section className="max-w-[1480px] mx-auto px-6 pb-20">
            <h3 className="serif-italic gold-gradient-text text-3xl mb-6">You may also like</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {related.map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
};

export default ProductDetail;
